import React, { useCallback, useEffect, useState } from "react";
import api from "../api";
import { downloadReport } from "../download";

const statusPill = (s) => {
  const x = String(s).toLowerCase();
  const cls = x === "published" ? "ok" : x === "approved" ? "ok" : x === "archived" ? "" : x.includes("review") ? "warn" : "";
  return <span className={`pill ${cls}`}>{s}</span>;
};
const nextStatus = { Draft: "Under Review", "Under Review": "Approved", Approved: "Published" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

export default function Policies() {
  const [policies, setPolicies] = useState([]);
  const [sel, setSel] = useState(null);
  const [versions, setVersions] = useState([]);
  const [newPolicy, setNewPolicy] = useState(false);
  const [pForm, setPForm] = useState({ title: "", type: "Policy", category: "", owner: "", review_date: "" });
  const [ver, setVer] = useState({ content: "", change_note: "" });

  const loadPolicies = useCallback(async () => {
    const r = await api.get("/policies"); setPolicies(r.data?.data || []);
  }, []);
  const loadVersions = useCallback(async (id) => {
    const r = await api.get(`/policies/${id}/versions`); setVersions(r.data?.data || []);
  }, []);

  useEffect(() => { loadPolicies(); }, [loadPolicies]);
  useEffect(() => { if (sel) loadVersions(sel.id); }, [sel, loadVersions]);

  const createPolicy = async (e) => {
    e.preventDefault();
    const r = await api.post("/policies", pForm);
    setNewPolicy(false); setPForm({ title: "", type: "Policy", category: "", owner: "", review_date: "" });
    await loadPolicies(); setSel(r.data?.data);
  };
  const addVersion = async (e) => {
    e.preventDefault();
    if (!ver.content.trim()) return;
    await api.post(`/policies/${sel.id}/versions`, ver);
    setVer({ content: "", change_note: "" });
    await loadVersions(sel.id); await loadPolicies();
    setSel((s) => policies.find((p) => p.id === s.id) || s);
  };
  const transition = async (v, status) => {
    const note = window.prompt(`Note for "${status}" (optional):`) ?? "";
    await api.post(`/policies/versions/${v.id}/transition`, { status, note });
    await loadVersions(sel.id); await loadPolicies();
  };
  const delPolicy = async (p) => { if (window.confirm(`Delete "${p.title}" and all its versions?`)) { await api.delete(`/policies/${p.id}`); setSel(null); loadPolicies(); } };

  return (
    <div>
      <h1>Policies & Privacy Notices</h1>
      <p className="sub">Versioned documents with an approval workflow — Draft → Under Review → Approved → Published.</p>

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16, alignItems: "start" }}>
        <div className="card">
          <div className="toolbar">
            <b style={{ flex: 1 }}>Documents</b>
            <button className="btn sm" onClick={() => downloadReport("policies", "xlsx")}>Excel</button>
            <button className="btn primary sm" onClick={() => setNewPolicy(true)}>+ New</button>
          </div>
          <div style={{ maxHeight: "60vh", overflow: "auto" }}>
            {policies.map((p) => (
              <button key={p.id} className="btn link" style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: sel?.id === p.id ? "#0f766e" : "#334155" }} onClick={() => setSel(p)}>
                <div style={{ fontWeight: sel?.id === p.id ? 700 : 500 }}>{p.title}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{p.type} · v{p.current_version} · {p.status}</div>
              </button>
            ))}
            {!policies.length && <div className="empty">No documents yet.</div>}
          </div>
        </div>

        <div>
          {!sel && <div className="card"><div className="empty">Select a document, or create one.</div></div>}
          {sel && (
            <>
              <div className="card" style={{ padding: 16, marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <h2 style={{ margin: 0, fontSize: 18 }}>{sel.title}</h2>
                  {statusPill(sel.status)}
                  <div style={{ flex: 1 }} />
                  <button className="btn link sm danger" onClick={() => delPolicy(sel)}>Delete</button>
                </div>
                <div style={{ color: "#64748b", fontSize: 13, marginTop: 6 }}>
                  {sel.type} · {sel.category || "—"} · current v{sel.current_version} · owner {sel.owner || "—"} · effective {fmt(sel.effective_date)} · next review {fmt(sel.review_date)}
                </div>
              </div>

              <div className="card" style={{ marginBottom: 12 }}>
                <div className="toolbar"><b>Version history</b></div>
                <div className="tablewrap">
                  <table>
                    <thead><tr><th>Ver</th><th>Status</th><th>By</th><th>Approved</th><th>Published</th><th>Change note</th><th style={{ width: 200 }}></th></tr></thead>
                    <tbody>
                      {versions.map((v) => (
                        <tr key={v.id}>
                          <td><b>v{v.version}</b></td>
                          <td>{statusPill(v.status)}</td>
                          <td>{v.created_by || "—"}</td>
                          <td>{v.approved_by ? `${v.approved_by}` : "—"}</td>
                          <td>{fmt(v.published_at)}</td>
                          <td style={{ maxWidth: 220, whiteSpace: "normal", color: "#64748b" }}>{v.change_note || "—"}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {nextStatus[v.status] && <button className="btn link sm" onClick={() => transition(v, nextStatus[v.status])}>→ {nextStatus[v.status]}</button>}
                            {(v.status === "Under Review") && <button className="btn link sm danger" onClick={() => transition(v, "Draft")}>Reject</button>}
                          </td>
                        </tr>
                      ))}
                      {!versions.length && <tr><td colSpan={7}><div className="empty">No versions — add the first below.</div></td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              <form className="card" style={{ padding: 16 }} onSubmit={addVersion}>
                <b>New version</b>
                <div className="form-row" style={{ marginTop: 8 }}><label>Content</label><textarea style={{ minHeight: 120 }} value={ver.content} onChange={(e) => setVer({ ...ver, content: e.target.value })} placeholder="Policy / notice text…" /></div>
                <div className="form-row"><label>Change note</label><input value={ver.change_note} onChange={(e) => setVer({ ...ver, change_note: e.target.value })} /></div>
                <button className="btn primary" type="submit">Add version (v{(sel.current_version || 0) + 1})</button>
              </form>
            </>
          )}
        </div>
      </div>

      {newPolicy && (
        <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && setNewPolicy(false)}>
          <form className="modal" onSubmit={createPolicy}>
            <h3>New Policy / Notice</h3>
            <div className="form-row"><label>Title</label><input value={pForm.title} onChange={(e) => setPForm({ ...pForm, title: e.target.value })} autoFocus /></div>
            <div className="grid2">
              <div className="form-row"><label>Type</label>
                <select value={pForm.type} onChange={(e) => setPForm({ ...pForm, type: e.target.value })}>
                  {["Policy", "Privacy Notice", "Customer Notice", "Employee Notice", "Vendor Notice", "Website"].map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div className="form-row"><label>Category</label><input value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} /></div>
              <div className="form-row"><label>Owner</label><input value={pForm.owner} onChange={(e) => setPForm({ ...pForm, owner: e.target.value })} /></div>
              <div className="form-row"><label>Next review</label><input type="date" value={pForm.review_date} onChange={(e) => setPForm({ ...pForm, review_date: e.target.value })} /></div>
            </div>
            <div className="foot">
              <button type="button" className="btn" onClick={() => setNewPolicy(false)}>Cancel</button>
              <button type="submit" className="btn primary">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
